/**
 * Che cosa si ricava dalla cartella scelta, senza toccare la rete.
 *
 * Sta a parte dalla schermata perche' e' l'unico pezzo del caricamento che si
 * puo' provare davvero: il dialogo di scelta della cartella non si comanda da
 * una prova, ma il modo in cui i nomi diventano un inventario si', e sbagliarlo
 * significa spedire novanta file con percorsi che il servizio non riconosce.
 *
 * **La radice e' dove sta `dataset.json`**, non la cartella che l'operatore ha
 * cliccato. Sono due cose diverse ogni volta che si sceglie una cartella di
 * troppo — un caso normale, non un errore — e prendere la seconda per la prima
 * darebbe percorsi con un livello in piu' rispetto a quelli del manifesto.
 */

/** Un file scelto, nella forma in cui l'inventario lo dichiara. */
export interface Declared {
  path: string
  size: number
}

/** La cartella letta: la radice, l'inventario e il manifesto ancora da leggere. */
export interface Chosen {
  root: string
  files: Declared[]
  manifest: File | null
  /** I file rimasti fuori dalla radice: si mostrano, non si spediscono. */
  outside: string[]
  /** Il file vero dietro ogni percorso: e' cio' che poi si spedisce, e non si
   *  puo' ricostruire da un nome — il navigatore lo consegna una volta sola,
   *  alla scelta della cartella. */
  blobs: Map<string, File>
}

const MANIFEST = 'dataset.json'

/**
 * Il percorso relativo del file scelto.
 *
 * Il navigatore lo attribuisce da solo (`webkitRelativePath`) quando la
 * cartella e' scelta dal selettore. Un file arrivato per trascinamento non ce
 * l'ha — il navigatore consegna un albero di voci, e il percorso lo si
 * ricostruisce scendendo l'albero — e allora lo porta in `relativePath`,
 * messo da chi ha letto l'albero. Prima quello, perche' e' l'unico dei due
 * che chi trascina puo' avere.
 */
function pathOf(file: File): string {
  const tagged = file as File & { relativePath?: string; webkitRelativePath?: string }
  if (tagged.relativePath && tagged.relativePath.length > 0) return tagged.relativePath
  const relative = tagged.webkitRelativePath
  return relative && relative.length > 0 ? relative : file.name
}

/** Attribuisce a un file il percorso con cui e' stato trovato nell'albero. */
export function withPath(file: File, relativePath: string): File {
  Object.defineProperty(file, 'relativePath', { value: relativePath, enumerable: false })
  return file
}

/**
 * Ordina i file scelti attorno al manifesto.
 *
 * Senza manifesto la radice resta vuota e l'inventario e' tutto cio' che c'e':
 * la schermata deve poter dire «in questa cartella non c'e' `dataset.json`»
 * mostrando comunque che cosa ha visto, altrimenti chi carica non sa se ha
 * sbagliato cartella o se il pacchetto e' incompleto.
 */
export function collect(chosen: readonly File[]): Chosen {
  // Il manifesto piu' alto: un pacchetto ne puo' contenere un altro piu' in
  // basso, e la radice e' quella del pacchetto che si sta caricando.
  let manifest: File | null = null
  let root = ''
  for (const file of chosen) {
    const path = pathOf(file)
    if (!path.endsWith(MANIFEST)) continue
    const prefix = path.slice(0, path.length - MANIFEST.length)
    if (manifest === null || prefix.length < root.length) {
      manifest = file
      root = prefix
    }
  }

  const files: Declared[] = []
  const outside: string[] = []
  const blobs = new Map<string, File>()
  for (const file of chosen) {
    const path = pathOf(file)
    if (root !== '' && !path.startsWith(root)) {
      outside.push(path)
      continue
    }
    const relative = path.slice(root.length)
    // Il manifesto entra nell'inventario come tutto il resto: l'acquisizione lo
    // rilegge dall'archivio (`phases.read_manifest`), e tenerlo fuori
    // significherebbe caricare un pacchetto che poi non si sa leggere. Il fatto
    // che il servizio l'abbia gia' visto per controllarlo non lo mette
    // nell'archivio.
    if (ignored(relative)) continue
    files.push({ path: relative, size: file.size })
    blobs.set(relative, file)
  }

  return { root, files, manifest, outside, blobs }
}

/**
 * Il rumore che il sistema operativo lascia nelle cartelle.
 *
 * Si toglie qui e non al servizio: sono file che non appartengono al volo,
 * spedirli costa banda e li fa comparire nell'inventario di un pacchetto
 * contrattuale.
 */
function ignored(path: string): boolean {
  const name = path.slice(path.lastIndexOf('/') + 1)
  return name === '.DS_Store' || name === 'Thumbs.db' || name.startsWith('._')
}

/** La somma dei byte, per dire quanto durera' prima di cominciare. */
export function weight(files: readonly Declared[]): number {
  return files.reduce((total, one) => total + one.size, 0)
}

